import React, { useState, useEffect } from 'react';
import { User, Class, StudentSummary, Lesson, GeneratedCredential, ParentStudentLink, ClassAnalyticsData, KeyAnalytics, StudentKeyAnalytics } from '../../types';
import LoadingSpinner from '../ui/LoadingSpinner';
import StudentDetailModal from '../modals/StudentDetailModal';
import GeneratedCredentialsModal from '../modals/GeneratedCredentialsModal';
import InviteParentModal from '../modals/InviteParentModal';
import ConfirmationModal from '../modals/ConfirmationModal';
import { api } from '../../utils/api';
import { KEY_TO_FINGER, FINGER_COLORS } from '../../constants';


// --- NEW: Analytics Components ---

const KeyboardHeatmap: React.FC<{ data: KeyAnalytics }> = ({ data }) => {
    const keyboardLayout = [
        ['`', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '='],
        ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', '[', ']', '\\'],
        ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', ';', "'"],
        ['z', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.', '/'],
    ];

    // FIX: Explicitly cast Object.values to number[] as TypeScript may infer it as unknown[].
    const maxErrors = Math.max(1, ...Object.values(data) as number[]);

    const getColor = (char: string) => {
        const count = data[char.toLowerCase()] || 0;
        if (count === 0) return 'bg-gray-200';
        const intensity = Math.min(1, count / maxErrors);
        
        // Interpolate between light yellow and dark red
        const r = Math.round(254 + intensity * (185 - 254));
        const g = Math.round(249 + intensity * (28 - 249));
        const b = Math.round(195 + intensity * (28 - 195));
        
        return `rgb(${r}, ${g}, ${b})`;
    };
    
    return (
        <div className="w-full p-2 bg-gray-100 rounded-lg">
            <h4 className="text-lg font-semibold text-center text-gray-700 mb-2">Class Problem Key Heatmap</h4>
            {keyboardLayout.map((row, rowIndex) => (
                <div key={rowIndex} className="flex justify-center">
                    {row.map((key) => (
                        <div key={key}
                             className="h-10 md:h-12 flex-1 m-1 rounded-md font-mono font-semibold text-gray-800 flex items-center justify-center shadow transition-colors"
                             style={{ backgroundColor: getColor(key) }}
                        >
                            {key}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

// --- NEW: Horizontal Bar Chart for Character Errors ---
const CharacterErrorChart: React.FC<{ data: KeyAnalytics }> = ({ data }) => {
    const sortedData = Object.entries(data)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10);
    const maxCount = sortedData.length > 0 ? sortedData[0][1] : 1;

    return (
        <div className="w-full mt-8">
            <h4 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">Most Missed Characters</h4>
            <div className="space-y-3">
                {sortedData.length > 0 ? sortedData.map(([char, count]) => (
                    <div key={char} className="flex items-center group">
                        <span className="w-8 font-mono font-bold text-gray-600 uppercase text-center bg-gray-100 rounded mr-2 py-1">{char}</span>
                        <div className="flex-grow bg-gray-200 rounded-sm h-5 overflow-hidden flex items-center">
                            <div className="bg-blue-400 h-full transition-all duration-1000 ease-out group-hover:bg-blue-500" style={{ width: `${(count / maxCount) * 100}%` }}></div>
                        </div>
                        <span className="w-16 text-right text-sm font-semibold text-gray-600">{count} errors</span>
                    </div>
                )) : <p className="text-sm text-gray-500 italic">No error data available yet.</p>}
            </div>
        </div>
    );
};

// --- NEW: Class Placement Histogram (Speed) ---
const SpeedHistogram: React.FC<{ students: StudentSummary[] }> = ({ students }) => {
    const buckets = { '0-9': 0, '10-19': 0, '20-29': 0, '30-39': 0, '40-49': 0, '50+': 0 };
    students.forEach(s => {
        const wpm = Number(s.average_wpm) || 0;
        if (wpm < 10) buckets['0-9']++;
        else if (wpm < 20) buckets['10-19']++;
        else if (wpm < 30) buckets['20-29']++;
        else if (wpm < 40) buckets['30-39']++;
        else if (wpm < 50) buckets['40-49']++;
        else buckets['50+']++;
    });
    const maxStudentsInBucket = Math.max(...Object.values(buckets), 1);
    
    const validStudents = students.filter(s => Number(s.average_wpm) > 0);
    const avgSpeed = validStudents.length > 0 ? Math.round(validStudents.reduce((acc, s) => acc + Number(s.average_wpm), 0) / validStudents.length) : 0;
    const topSpeed = students.length > 0 ? Math.max(...students.map(s => Number(s.average_wpm) || 0), 0) : 0;

    return (
        <div className="w-full bg-white rounded-lg shadow border border-gray-100 p-6 flex flex-col h-full">
            <div className="flex justify-between items-end mb-8">
                <div>
                    <h4 className="text-xl font-bold text-gray-800">Class Placement: Speed</h4>
                    <div className="flex items-baseline mt-2">
                        <span className="text-4xl font-extrabold text-blue-600">{avgSpeed}</span>
                        <span className="text-md font-medium text-gray-500 ml-1">WPM (Avg)</span>
                    </div>
                </div>
                <div className="text-right bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Highest</p>
                    <p className="text-xl font-bold text-gray-800">{topSpeed} WPM</p>
                </div>
            </div>
            <div className="flex-grow flex items-end space-x-1 sm:space-x-2 border-b-2 border-l-2 border-gray-200 pb-1 pl-1 pt-10 relative">
                <div className="absolute -left-6 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-400 py-1">
                    <span>{maxStudentsInBucket}</span>
                    <span>{Math.ceil(maxStudentsInBucket / 2)}</span>
                    <span>0</span>
                </div>
                {Object.entries(buckets).map(([label, count]) => (
                    <div key={label} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                        <div className="opacity-0 group-hover:opacity-100 absolute -top-8 bg-gray-800 text-white text-xs py-1 px-2 rounded transition-opacity whitespace-nowrap z-10 pointer-events-none">
                            {count} student{count !== 1 ? 's' : ''}
                        </div>
                        <div className="w-full bg-blue-300 group-hover:bg-blue-500 transition-all duration-500 rounded-t-sm" style={{ height: `${(count / maxStudentsInBucket) * 100}%`, minHeight: count > 0 ? '4px' : '0' }}></div>
                        <span className="text-xs text-gray-500 mt-2 font-medium">{label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const AnalyticsTab: React.FC<{ data: ClassAnalyticsData | null, students: StudentSummary[], onStudentClick: (student: StudentSummary) => void; }> = ({ data, students, onStudentClick }) => {
    if (!data) return <LoadingSpinner message="Loading analytics..." />;

    return (
        <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <SpeedHistogram students={students} />
                <div className="bg-white p-6 rounded-lg shadow border border-gray-100">
                    <KeyboardHeatmap data={data.class_problem_keys} />
                    <CharacterErrorChart data={data.class_problem_keys} />
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow border border-gray-100">
                <h3 className="text-2xl font-semibold mb-4 text-gray-800">Student-Specific Analysis</h3>
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Student Name</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Username</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Lessons Completed</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-red-500 uppercase tracking-wider">Most Missed Key</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Error Count</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {data.student_analytics.length > 0 ? data.student_analytics.map(s => {
                                const fullStudent = students.find(student => student.user_id === s.user_id);
                                return (
                                    <tr key={s.user_id} className="hover:bg-blue-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600 hover:text-blue-800 cursor-pointer" onClick={() => {
                                            if (fullStudent) onStudentClick(fullStudent);
                                        }}>
                                            {s.first_name} {s.last_name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{(fullStudent as any)?.username || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">{fullStudent?.lessons_completed || 0}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <span className="bg-red-100 text-red-700 px-3 py-1 rounded font-mono font-bold uppercase">{s.most_missed_key}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-700">{s.error_count}</td>
                                    </tr>
                                );
                            }) : (
                                <tr><td colSpan={5} className="text-center py-8 text-gray-500 italic">No specific error data available for students yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// --- END: Analytics Components ---


interface TeacherDashboardProps {
  user: User;
}

const tutorialBlocks = [
    { name: "Home Row & Introduction", start: 1, end: 23, ids: [] as number[] },
    { name: "Top Row", start: 24, end: 51, ids: [] as number[] },
    { name: "Bottom Row", start: 52, end: 88, ids: [] as number[] },
    { name: "Basic Level 1", start: 89, end: 126, ids: [] as number[] },
    { name: "Tricky Words 1", start: 127, end: 137, ids: [] as number[] },
    { name: "Shift Key", start: 138, end: 191, ids: [] as number[] },
    { name: "Common Patterns 1", start: 192, end: 202, ids: [] as number[] },
    { name: "Basic Level 2", start: 203, end: 233, ids: [] as number[] },
    { name: "Tricky Words 2", start: 234, end: 244, ids: [] as number[] },
    { name: "Numbers", start: 245, end: 274, ids: [] as number[] },
    { name: "Common Patterns 2", start: 275, end: 285, ids: [] as number[] },
    { name: "Basic Level 3", start: 286, end: 316, ids: [] as number[] },
    { name: "Symbols", start: 317, end: 346, ids: [] as number[] },
    { name: "Common Patterns 3", start: 347, end: 357, ids: [] as number[] },
    { name: "Advanced Level 1", start: 358, end: 388, ids: [] as number[] },
    { name: "More Symbols", start: 389, end: 418, ids: [] as number[] },
    { name: "Tricky Words 3", start: 419, end: 429, ids: [] as number[] },
    { name: "Advanced Level 2", start: 430, end: 460, ids: [] as number[] },
    { name: "Advanced Level 3", start: 461, end: 491, ids: [] as number[] },
    { name: "Advanced Level 4", start: 492, end: 524, ids: [] as number[] },
    { name: "Advanced Level 5", start: 525, end: 557, ids: [] as number[] },
    { name: "Advanced Level 6", start: 558, end: 590, ids: [] as number[] },
    { name: "Advanced Level 7", start: 591, end: 621, ids: [] as number[] },
    { name: "Advanced Level 8", start: 622, end: 652, ids: [] as number[] },
    { name: "Advanced Level 9", start: 653, end: 670, ids: [] as number[] },
];

const PasswordOptions: React.FC<{
    passwordOption: 'generate' | 'custom';
    setPasswordOption: (option: 'generate' | 'custom') => void;
    customPassword?: string;
    setCustomPassword: (password: string) => void;
}> = ({ passwordOption, setPasswordOption, customPassword, setCustomPassword }) => (
    <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Password Option</label>
        <div className="flex space-x-4">
            <label className="flex items-center"><input type="radio" name="passwordOption" value="custom" checked={passwordOption === 'custom'} onChange={() => setPasswordOption('custom')} className="mr-2" /> One Password for the Whole Class</label>
            <label className="flex items-center"><input type="radio" name="passwordOption" value="generate" checked={passwordOption === 'generate'} onChange={() => setPasswordOption('generate')} className="mr-2" /> Auto-Generate a Different Password for Each Student</label>
        </div>
        {passwordOption === 'custom' && (
            <div className="mt-2">
                <label className="sr-only" htmlFor="customPassword">Custom Password</label>
                <input id="customPassword" type="text" value={customPassword} onChange={(e) => setCustomPassword(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" placeholder="Enter a password for all students" />
            </div>
        )}
    </div>
);


const TeacherDashboard: React.FC<TeacherDashboardProps> = ({ user }) => {
    const [classes, setClasses] = useState<Class[]>([]);
    const [selectedClass, setSelectedClass] = useState<Class | null>(null);
    const [students, setStudents] = useState<StudentSummary[]>([]);
    const [parentLinks, setParentLinks] = useState<ParentStudentLink[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tab, setTab] = useState('progress');
    const [viewingStudent, setViewingStudent] = useState<StudentSummary | null>(null);
    const [teacherLessons, setTeacherLessons] = useState<Lesson[]>([]);
    const [systemTutorialLessons, setSystemTutorialLessons] = useState<Lesson[]>([]);
    const [editingLesson, setEditingLesson] = useState<Lesson | null>(null);
    const [lessonTitle, setLessonTitle] = useState("");
    const [lessonText, setLessonText] = useState("");
    const [assignedLessons, setAssignedLessons] = useState<Lesson[]>([]);
    const [unassignedCustomLessons, setUnassignedCustomLessons] = useState<Lesson[]>([]);
    const [assignableBlocks, setAssignableBlocks] = useState(tutorialBlocks);
    const [newClassName, setNewClassName] = useState("");
    const [newClassUsernamePrefix, setNewClassUsernamePrefix] = useState("");
    const [prefixInput, setPrefixInput] = useState("");

    const [bulkStudentCount, setBulkStudentCount] = useState(1);
    const [generatedCredentials, setGeneratedCredentials] = useState<GeneratedCredential[]>([]);
    const [isCredentialsModalOpen, setCredentialsModalOpen] = useState(false);
    
    const [sessionCode, setSessionCode] = useState<{code: string, lessonTitle: string, expires: string, link: string} | null>(null);
    const [sessionLessonId, setSessionLessonId] = useState<string>("");
    const [manageError, setManageError] = useState<string | null>(null);
    const [manageSuccess, setManageSuccess] = useState<string | null>(null);
    const [invitingStudent, setInvitingStudent] = useState<StudentSummary | null>(null);
    const [copyStatus, setCopyStatus] = useState(false);
    const [analyticsData, setAnalyticsData] = useState<ClassAnalyticsData | null>(null);
    const [lessonToAssign, setLessonToAssign] = useState<string>("");

    const [studentToDelete, setStudentToDelete] = useState<StudentSummary | null>(null);
    const [lessonToDelete, setLessonToDelete] = useState<Lesson | null>(null);
    const [studentToReset, setStudentToReset] = useState<StudentSummary | null>(null);
    const [passwordOption, setPasswordOption] = useState<'generate' | 'custom'>('custom');
    const [customPassword, setCustomPassword] = useState("");

    const fetchTeacherData = async () => {
        try {
            const [myLessons, systemLessons] = await Promise.all([
                api.get<Lesson[]>('/api/v1/teacher/lessons'),
                api.get<Lesson[]>('/api/v1/lessons/system')
            ]);
            setTeacherLessons(myLessons);
            setSystemTutorialLessons(systemLessons);
        } catch (err) {
            console.error("Error fetching teacher lessons:", err);
            setError(err instanceof Error ? err.message : "An unknown error occurred.");
        }
    };


    const fetchStudentsForClass = async (classId: string) => {
        try {
            const data = await api.get<StudentSummary[]>(`/api/v1/teacher/class/${classId}/students`);
            setStudents(data);
        } catch(err) {
            console.error("Error fetching students:", err);
            setError(err instanceof Error ? err.message : "An unknown error occurred while fetching students.");
        }
    }
    
     const fetchParentsForClass = async (classId: string) => {
        try {
            const data = await api.get<ParentStudentLink[]>(`/api/v1/teacher/class/${classId}/parents`);
            setParentLinks(data);
        } catch(err) {
            console.error("Error fetching parents:", err);
        }
    }

    // Fetch non-class-specific data like classes and teacher-created lessons
    useEffect(() => {
        if (!user) return;
        setLoading(true);

        const fetchInitialData = async () => {
            try {
                const classesData = await api.get<Class[]>(`/api/v1/teacher/${user.user_id}/classes`);
                await fetchTeacherData();
                setClasses(classesData);

                if (classesData.length > 0) {
                    const classToSelect = selectedClass ? classesData.find(c => c.class_id === selectedClass.class_id) : classesData[0];
                    setSelectedClass(classToSelect || classesData[0]);
                } else {
                    setSelectedClass(null);
                    setLoading(false); // No class, no more loading needed
                }
            } catch (err) {
                console.error("Error fetching initial teacher data:", err);
                setError(err instanceof Error ? err.message : "An unknown error occurred.");
                setLoading(false);
            }
        };

        fetchInitialData();
    }, [user]);

    // Set a default lesson ID for the live session dropdown once data is available
    useEffect(() => {
        if (!sessionLessonId && (teacherLessons.length > 0 || systemTutorialLessons.length > 0)) {
            const defaultId = teacherLessons[0]?.lesson_id.toString() || systemTutorialLessons[0]?.lesson_id.toString();
            setSessionLessonId(defaultId);
        }
    }, [teacherLessons, systemTutorialLessons, sessionLessonId]);

    const fetchDataForClass = async (classId: string) => {
        setLoading(true);
        setError(null);
        setAnalyticsData(null);
        try {
            await Promise.all([
                fetchStudentsForClass(classId),
                fetchParentsForClass(classId)
            ]);
            
            const [customLessons, systemLessons, assigned, analytics] = await Promise.all([
                api.get<Lesson[]>('/api/v1/teacher/lessons'), // This teacher's own custom lessons only
                api.get<Lesson[]>('/api/v1/lessons/system'),
                api.get<Lesson[]>(`/api/v1/class/${classId}/assignments`),
                api.get<ClassAnalyticsData>(`/api/v1/teacher/class/${classId}/analytics`).catch(err => {
                    console.warn("Analytics endpoint failed.", err.message);
                    return null;
                })
            ]);
            setAnalyticsData(analytics);

            const assignedIds = new Set(assigned.map((l: Lesson) => l.lesson_id));

            setUnassignedCustomLessons(customLessons.filter(l => !assignedIds.has(l.lesson_id)));
            setAssignedLessons(assigned);
            
            // Process blocks
            const blocks = JSON.parse(JSON.stringify(tutorialBlocks)) as typeof tutorialBlocks; // Deep copy
            blocks.forEach(b => b.ids = []); // Reset IDs

            for (const lesson of systemLessons) {
                const num = parseInt(lesson.system_lesson_id!.split('-')[1]);
                for (const block of blocks) {
                    if (num >= block.start && num <= block.end) {
                        block.ids.push(Number(lesson.lesson_id));
                        break;
                    }
                }
            }
            setAssignableBlocks(blocks);

        } catch (err) {
            console.error("Error fetching class data:", err);
            setError(err instanceof Error ? err.message : "An unknown error occurred while fetching class data.");
        } finally {
            setLoading(false);
        }
    };


    // Fetch data specific to the selected class
    useEffect(() => {
        if (!selectedClass) {
            setStudents([]);
            setParentLinks([]);
            setAnalyticsData(null);
            setAssignedLessons([]);
            setUnassignedCustomLessons([]);
            return;
        }
        fetchDataForClass(selectedClass.class_id);
    }, [selectedClass]);

    useEffect(() => {
        if (unassignedCustomLessons.length > 0 && !lessonToAssign) {
            setLessonToAssign(unassignedCustomLessons[0].lesson_id.toString());
        } else if (unassignedCustomLessons.length === 0) {
            setLessonToAssign("");
        }
    }, [unassignedCustomLessons, lessonToAssign]);


    const handleTabChange = (newTab: string) => {
        setTab(newTab);
        setError(null);
        setManageError(null);
        setManageSuccess(null);
        setSessionCode(null);
    };
    
    const handleCreateSingleStudent = async (e: React.FormEvent) => {
        e.preventDefault();
        setManageError(null);
        setManageSuccess(null);
        if (!selectedClass) return;
        if (passwordOption === 'custom' && !customPassword) {
            setManageError("Please enter a class password or choose to auto-generate one.");
            return;
        }

        try {
            setLoading(true);
            const result = await api.post<GeneratedCredential>(`/api/v1/teacher/class/${selectedClass.class_id}/students`, {
                password: passwordOption === 'custom' ? customPassword : undefined
            });

            const newCredentials: GeneratedCredential[] = [{
                fullName: `${result.fullName}`,
                username: result.username,
                password: result.password
            }];

            setGeneratedCredentials(newCredentials);
            setCredentialsModalOpen(true);
            setManageSuccess(`Student "${result.username}" has been created.`);
            await fetchStudentsForClass(selectedClass.class_id);
        } catch (err) {
            console.error("Error creating single student:", err);
            setManageError(err instanceof Error ? err.message : "An unknown error occurred during student creation.");
        } finally {
            setLoading(false);
        }
    };
    
    const handleCreateBulkStudents = async (e: React.FormEvent) => {
        e.preventDefault();
        setManageError(null);
        setManageSuccess(null);
        if (!selectedClass || bulkStudentCount < 1) return;
        if (passwordOption === 'custom' && !customPassword) {
            setManageError("Please enter a class password or choose to auto-generate one.");
            return;
        }

        try {
            setLoading(true);
            const result = await api.post<{ created: GeneratedCredential[]; skipped: { name: string; reason: string }[] }>(`/api/v1/teacher/class/${selectedClass.class_id}/students/bulk`, {
                count: bulkStudentCount,
                password: passwordOption === 'custom' ? customPassword : undefined
            });

            setGeneratedCredentials(result.created);
            setCredentialsModalOpen(true);
            setManageSuccess(`${result.created.length} students have been created.`);
            await fetchStudentsForClass(selectedClass.class_id);
        } catch (err) {
            console.error("Error creating bulk students:", err);
            setManageError(err instanceof Error ? err.message : "An unknown error occurred during bulk creation.");
        } finally {
            setLoading(false);
        }
    };
    
    const handleDeleteStudent = async () => {
        if (!studentToDelete) return;
        setManageError(null);
        try {
            await api.delete(`/api/v1/teacher/students/${studentToDelete.user_id}`);
            setStudents(prev => prev.filter(s => s.user_id !== studentToDelete.user_id));
            setManageSuccess(`${studentToDelete.first_name} has been deleted.`);
        } catch (err) {
            setManageError(err instanceof Error ? err.message : "Failed to delete student.");
        } finally {
            setStudentToDelete(null);
        }
    };

    const submitPasswordReset = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!studentToReset) return;
        try {
            setLoading(true);
            const result = await api.post<GeneratedCredential>(`/api/v1/teacher/students/${studentToReset.user_id}/reset-password`, {
                password: passwordOption === 'custom' ? customPassword : undefined
            });
            
            setGeneratedCredentials([{
                fullName: `${studentToReset.first_name} ${studentToReset.last_name}`,
                username: result.username || (studentToReset as any).username, 
                password: result.password
            }]);
            
            setCredentialsModalOpen(true);
            setStudentToReset(null);
            setPasswordOption('generate');
            setCustomPassword("");
            setTab('manage'); // Switch to manage tab so they see success message clearly
            setManageSuccess(`Password reset successfully for ${studentToReset.first_name}.`);
        } catch (err) {
            console.error("Error resetting password:", err);
            setManageError(err instanceof Error ? err.message : "Failed to reset password. Check backend connection.");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveLesson = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!lessonTitle || !lessonText) return;
        const lessonData = { title: lessonTitle, lesson_text: lessonText };
        
        try {
            if (editingLesson) {
                await api.put<Lesson>(`/api/v1/lessons/${editingLesson.lesson_id}`, lessonData);
            } else {
                await api.post<Lesson>('/api/v1/lessons', lessonData);
            }
            
            setEditingLesson(null);
            setLessonTitle("");
            setLessonText("");
            await fetchTeacherData(); // Refresh the list of teacher's lessons
            if (selectedClass) await fetchDataForClass(selectedClass.class_id); // Also refresh assignment data
        } catch (err) {
            console.error("Error saving lesson:", err);
            setError(err instanceof Error ? err.message : "An unknown error occurred while saving the lesson.");
        }
    };

    const handleDeleteLesson = async () => {
        if (!lessonToDelete || !selectedClass) return;
        try {
            await api.delete(`/api/v1/lessons/${lessonToDelete.lesson_id}`);
            await fetchTeacherData();
            await fetchDataForClass(selectedClass.class_id);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete lesson.');
        } finally {
            setLessonToDelete(null);
        }
    };

    const handleCreateClass = async (e: React.FormEvent) => {
        e.preventDefault();
        setManageError(null);
        setManageSuccess(null);
        if (!newClassName || !newClassUsernamePrefix) return;
        try {
            await api.post<Class>('/api/v1/classes', { class_name: newClassName, grade_level: 'Mixed', username_prefix: newClassUsernamePrefix });
            // Refetch classes to update the list and dropdown
            const data = await api.get<Class[]>(`/api/v1/teacher/${user.user_id}/classes`);
            setClasses(data);
            setNewClassName("");
            setNewClassUsernamePrefix("");
            setManageSuccess("Class created successfully!");
        } catch (err) {
            console.error("Error creating class:", err);
            setManageError(err instanceof Error ? err.message : "An unknown error occurred while creating the class.");
        }
    };

    const handleSetPrefix = async (e: React.FormEvent) => {
        e.preventDefault();
        setManageError(null);
        setManageSuccess(null);
        if (!selectedClass || !prefixInput) return;
        try {
            await api.put<Class>(`/api/v1/classes/${selectedClass.class_id}/prefix`, { username_prefix: prefixInput });
            const data = await api.get<Class[]>(`/api/v1/teacher/${user.user_id}/classes`);
            setClasses(data);
            setSelectedClass(data.find(c => c.class_id === selectedClass.class_id) || null);
            setPrefixInput("");
            setManageSuccess("Username prefix set for this class.");
        } catch (err) {
            console.error("Error setting username prefix:", err);
            setManageError(err instanceof Error ? err.message : "An unknown error occurred while setting the username prefix.");
        }
    };

    const handleCopy = () => {
        if(!sessionCode) return;
        navigator.clipboard.writeText(sessionCode.link);
        setCopyStatus(true);
        setTimeout(() => setCopyStatus(false), 2000);
    }

    const handleGenerateCode = async () => {
        setManageError(null);
        setManageSuccess(null);
        setSessionCode(null);
        if (!sessionLessonId) {
            setManageError("Please select a lesson to generate a code for.");
            return;
        }
        try {
            const result = await api.post<{code: string; expires: string; link: string;}>('/api/v1/sessions/create', { lesson_id_string: sessionLessonId });
            
            const allLessons = [...systemTutorialLessons, ...teacherLessons];
            const selectedLesson = allLessons.find(l => l.lesson_id.toString() === sessionLessonId);
    
            setSessionCode({
                code: result.code,
                expires: new Date(result.expires).toLocaleTimeString(),
                link: result.link,
                lessonTitle: selectedLesson?.title || 'Unknown Lesson'
            });
            setManageSuccess("Session code generated successfully!");
        } catch (err) {
            console.error("Error generating session code:", err);
            setManageError(err instanceof Error ? err.message : "An unknown error occurred during code generation.");
        }
    };
    
    const handleAssignLesson = async () => {
        if (!lessonToAssign || !selectedClass) return;
        try {
            await api.post(`/api/v1/class/${selectedClass.class_id}/assign`, { lesson_id: Number(lessonToAssign) });
            await fetchDataForClass(selectedClass.class_id);
        } catch (err) {
            console.error("Error assigning lesson:", err);
            setError(err instanceof Error ? err.message : "An unknown error occurred during assignment.");
        }
    };
    
    const handleAssignBlock = async (block: typeof tutorialBlocks[0]) => {
        if (!selectedClass || block.ids.length === 0) return;
        try {
            await api.post(`/api/v1/class/${selectedClass.class_id}/assign-bulk`, { lesson_ids: block.ids });
            await fetchDataForClass(selectedClass.class_id);
        } catch (err) {
            console.error("Error assigning block:", err);
            setError(err instanceof Error ? err.message : "An unknown error occurred during block assignment.");
        }
    };

    const handleUnassignLesson = async (lesson: Lesson) => {
        if (!selectedClass) return;
        try {
            await api.delete(`/api/v1/class/${selectedClass.class_id}/unassign/${lesson.lesson_id}`);
            await fetchDataForClass(selectedClass.class_id);
        } catch (err) {
            console.error("Error unassigning lesson:", err);
            setError(err instanceof Error ? err.message : "An unknown error occurred during unassignment.");
        }
    };

    // RENDER FUNCTIONS
    const renderProgressTab = () => (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-2xl font-semibold mb-4">Student Roster & Progress</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lessons Completed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg. WPM</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg. Accuracy</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parent Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {students.length > 0 ? students.map(student => (
                  <tr key={student.user_id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 font-mono">{(student as any).username || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{student.lessons_completed}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{(Number(student.average_wpm) || 0).toFixed(1)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{(Number(student.average_accuracy) || 0).toFixed(1)}%</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {student.parent_linked
                            ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Linked</span>
                            : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">Not Linked</span>
                        }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button onClick={() => setViewingStudent(student)} className="text-blue-600 hover:text-blue-900">Details</button>
                      <button onClick={() => { setPasswordOption('generate'); setCustomPassword(""); setStudentToReset(student); }} className="text-orange-600 hover:text-orange-900">Reset Password</button>
                      {!student.parent_linked && <button onClick={() => setInvitingStudent(student)} className="text-green-600 hover:text-green-900">Invite</button>}
                      <button onClick={() => setStudentToDelete(student)} className="text-red-600 hover:text-red-900">Delete</button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={6} className="text-center py-4 text-gray-500">No students enrolled in this class yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
    );

    const renderBuilderTab = () => (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <form onSubmit={handleSaveLesson} className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-2xl font-semibold mb-4">{editingLesson ? "Edit Lesson" : "Create New Lesson"}</h3>
              <div className="mb-4">
                <label htmlFor="lessonTitle" className="block text-sm font-medium text-gray-700">Lesson Title</label>
                <input type="text" id="lessonTitle" value={lessonTitle} onChange={(e) => setLessonTitle(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" required />
              </div>
              <div className="mb-4">
                <label htmlFor="lessonText" className="block text-sm font-medium text-gray-700">Lesson Text</label>
                <textarea id="lessonText" value={lessonText} onChange={(e) => setLessonText(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md font-mono" rows={10} required />
              </div>
              <div className="flex justify-end space-x-2">
                {editingLesson && (<button type="button" onClick={() => { setEditingLesson(null); setLessonTitle(""); setLessonText("");}} className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600">Cancel</button>)}
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">{editingLesson ? "Save Changes" : "Create Lesson"}</button>
              </div>
            </form>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-2xl font-semibold mb-4">Your Created Lessons</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
            {teacherLessons.length > 0 ? teacherLessons.map(lesson => (
              <div key={lesson.lesson_id} className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                <span>{lesson.title}</span>
                <div className="space-x-2">
                  <button onClick={() => { setEditingLesson(lesson); setLessonTitle(lesson.title); setLessonText(lesson.lesson_text); }} className="text-sm text-blue-600 hover:underline">Edit</button>
                  <button onClick={() => setLessonToDelete(lesson)} className="text-sm text-red-600 hover:underline">Delete</button>
                </div>
              </div>
            )) : <p className="text-gray-500">You haven't created any lessons yet.</p>}
            </div>
          </div>
        </div>
      );
      
    const renderAssignTab = () => {
        const assignedIds = new Set(assignedLessons.map(l => l.lesson_id));

        return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <div className="bg-white p-6 rounded-lg shadow space-y-6">
                <div>
                    <h3 className="text-2xl font-semibold mb-4">Assign System Lesson Blocks</h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                        {assignableBlocks.map(block => {
                            const isFullyAssigned = block.ids.every(id => assignedIds.has(id));
                            return (
                                <div key={block.name} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                    <span>{block.name}</span>
                                    <button
                                        onClick={() => handleAssignBlock(block)}
                                        disabled={isFullyAssigned}
                                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                    >
                                        {isFullyAssigned ? "Assigned" : "Assign"}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
                 <div>
                    <h3 className="text-2xl font-semibold mb-4">Assign Custom Lesson</h3>
                    {unassignedCustomLessons.length > 0 ? (
                        <div className="flex items-center space-x-2">
                            <select
                                value={lessonToAssign}
                                onChange={(e) => setLessonToAssign(e.target.value)}
                                className="flex-grow p-2 border border-gray-300 rounded-md bg-white"
                                aria-label="Select a custom lesson to assign"
                            >
                                {unassignedCustomLessons.map(lesson => (
                                    <option key={lesson.lesson_id} value={lesson.lesson_id.toString()}>
                                        {lesson.title}
                                    </option>
                                ))}
                            </select>
                            <button
                                onClick={handleAssignLesson}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                                disabled={!lessonToAssign}
                            >
                                Assign
                            </button>
                        </div>
                    ) : (
                        <p className="text-gray-500">All your custom lessons have been assigned.</p>
                    )}
                </div>
            </div>
    
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-2xl font-semibold mb-4">Assigned to: {selectedClass?.class_name}</h3>
                <div className="space-y-2 min-h-[200px] max-h-[400px] overflow-y-auto p-2 bg-gray-50 rounded">
                    {assignedLessons.length > 0 ? [...assignedLessons].sort((a,b) => a.title.localeCompare(b.title)).map(lesson => (
                        <div key={lesson.lesson_id} className="flex justify-between items-center p-3 bg-green-50 rounded-md">
                            <span>{lesson.title}</span>
                            <button
                                onClick={() => handleUnassignLesson(lesson)}
                                className="px-3 py-1 text-sm bg-red-500 text-white rounded-md hover:bg-red-600"
                            >
                                Unassign
                            </button>
                        </div>
                    )) : <p className="text-gray-500 text-center pt-8">No lessons have been assigned to this class yet.</p>}
                </div>
            </div>
        </div>
    )};

    const renderManageTab = () => (
        <>
            {manageError && <div className="mb-4 text-red-600 bg-red-100 p-3 rounded-md text-center">{manageError}</div>}
            {manageSuccess && <div className="mb-4 text-green-600 bg-green-100 p-3 rounded-md text-center">{manageSuccess}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                <div className="space-y-6">
                    <form onSubmit={handleCreateClass} className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-2xl font-semibold mb-4">1. Create a New Class</h3>
                    <div className="mb-4">
                        <label htmlFor="className" className="block text-sm font-medium text-gray-700">Class Name</label>
                        <input type="text" id="className" value={newClassName} onChange={(e) => setNewClassName(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" placeholder="e.g., Period 2 Keyboarding" required />
                    </div>
                    <div className="mb-4">
                        <label htmlFor="classUsernamePrefix" className="block text-sm font-medium text-gray-700">Username Prefix (3-5 characters)</label>
                        <input type="text" id="classUsernamePrefix" value={newClassUsernamePrefix} onChange={(e) => setNewClassUsernamePrefix(e.target.value.toLowerCase())} className="mt-1 block w-full p-2 border border-gray-300 rounded-md font-mono" placeholder="e.g., cls5" minLength={3} maxLength={5} required />
                        <p className="text-xs text-gray-500 mt-1">Students in this class get usernames like {newClassUsernamePrefix || "cls5"}1, {newClassUsernamePrefix || "cls5"}2, etc. No student names are stored.</p>
                    </div>
                    <div className="flex justify-end"><button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Create Class</button></div>
                    </form>
                    {selectedClass && !selectedClass.username_prefix && (
                        <form onSubmit={handleSetPrefix} className="bg-yellow-50 border border-yellow-300 p-6 rounded-lg shadow">
                            <h3 className="text-lg font-semibold mb-2">Set a Username Prefix for "{selectedClass.class_name}"</h3>
                            <p className="text-sm text-gray-600 mb-2">This class was created before username prefixes existed. Set one to start adding students.</p>
                            <div className="flex gap-2">
                                <input type="text" value={prefixInput} onChange={(e) => setPrefixInput(e.target.value.toLowerCase())} className="block w-full p-2 border border-gray-300 rounded-md font-mono" placeholder="e.g., cls5" minLength={3} maxLength={5} required />
                                <button type="submit" className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 whitespace-nowrap">Set Prefix</button>
                            </div>
                        </form>
                    )}
                    {selectedClass && selectedClass.username_prefix && (
                    <form onSubmit={handleCreateSingleStudent} className="bg-white p-6 rounded-lg shadow">
                        <h3 className="text-2xl font-semibold mb-4">2. Add a Single Student</h3>
                        <p className="text-sm text-gray-600 mb-4">Adding to: <b className="text-gray-900">{selectedClass.class_name}</b> (usernames like <span className="font-mono">{selectedClass.username_prefix}1</span>). No student name is entered or stored.</p>
                        <PasswordOptions
                            passwordOption={passwordOption}
                            setPasswordOption={setPasswordOption}
                            customPassword={customPassword}
                            setCustomPassword={setCustomPassword}
                        />
                        <div className="flex justify-end"><button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700" disabled={loading}>{loading ? 'Creating...' : 'Add Student'}</button></div>
                    </form>
                    )}
                </div>
                {selectedClass && selectedClass.username_prefix && (
                <form onSubmit={handleCreateBulkStudents} className="bg-white p-6 rounded-lg shadow h-full flex flex-col">
                    <h3 className="text-2xl font-semibold mb-4">3. Add Multiple Students</h3>
                    <p className="text-sm text-gray-600 mb-4">Adding to: <b className="text-gray-900">{selectedClass.class_name}</b> (usernames like <span className="font-mono">{selectedClass.username_prefix}1</span>). No student names are entered or stored.</p>
                    <div className="mb-4">
                        <label htmlFor="bulk-student-count" className="block text-sm font-medium text-gray-700">Number of Students</label>
                        <input id="bulk-student-count" type="number" min={1} max={200} value={bulkStudentCount} onChange={(e) => setBulkStudentCount(parseInt(e.target.value, 10) || 1)} className="mt-1 block w-32 p-2 border border-gray-300 rounded-md" />
                    </div>
                    <PasswordOptions
                        passwordOption={passwordOption}
                        setPasswordOption={setPasswordOption}
                        customPassword={customPassword}
                        setCustomPassword={setCustomPassword}
                    />
                    <div className="flex justify-end"><button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700" disabled={loading}>{loading ? 'Creating...' : 'Create Students'}</button></div>
                </form>
                )}
            </div>
        </>
    );

    const renderLiveSessionsTab = () => {
        return (
            <div>
                 {manageError && <div className="mb-4 text-red-600 bg-red-100 p-3 rounded-md text-center">{manageError}</div>}
                 {manageSuccess && <div className="mb-4 text-green-600 bg-green-100 p-3 rounded-md text-center">{manageSuccess}</div>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                    <div className="bg-white p-6 rounded-lg shadow">
                        <h3 className="text-2xl font-semibold mb-4">Generate Session Code</h3>
                        <div className="mb-4">
                            <label htmlFor="session-lesson" className="block text-sm font-medium text-gray-700">Content to Share</label>
                            <select 
                                id="session-lesson" 
                                value={sessionLessonId}
                                onChange={(e) => setSessionLessonId(e.target.value)}
                                className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                                disabled={teacherLessons.length === 0 && systemTutorialLessons.length === 0}
                            >
                                <optgroup label="Your Custom Lessons">
                                    {teacherLessons.map(l => <option key={l.lesson_id} value={l.lesson_id.toString()}>{l.title}</option>)}
                                </optgroup>
                                <optgroup label="Tutorial Lessons">
                                    {systemTutorialLessons.map(l => <option key={l.lesson_id} value={l.lesson_id.toString()}>{l.title}</option>)}
                                </optgroup>
                            </select>
                        </div>
                        <div className="flex justify-end">
                            <button onClick={handleGenerateCode} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Generate 2-Hour Code</button>
                        </div>
                    </div>
                    {sessionCode && (
                        <div className="bg-green-50 p-6 rounded-lg shadow border border-green-200">
                            <h3 className="text-2xl font-semibold mb-4 text-green-800">Session Active!</h3>
                            <div className="space-y-3 text-gray-700">
                                <div>
                                    <p className="font-semibold">Share Code:</p>
                                    <p className="text-4xl font-mono tracking-widest bg-white p-2 rounded text-center">{sessionCode.code}</p>
                                </div>
                                <div>
                                    <p className="font-semibold">Share Link:</p>
                                    <div className="flex space-x-2">
                                        <input type="text" readOnly value={sessionCode.link} className="flex-grow p-2 border border-gray-300 rounded-md bg-gray-100" />
                                        <button onClick={handleCopy} className="px-3 py-2 bg-gray-200 rounded-md hover:bg-gray-300">{copyStatus ? 'Copied!' : 'Copy'}</button>
                                    </div>
                                </div>
                                <div>
                                    <p><span className="font-semibold">Content:</span> {sessionCode.lessonTitle}</p>
                                    <p><span className="font-semibold">Expires at:</span> {sessionCode.expires}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    };
    
    const renderParentsTab = () => {
    const unlinkedStudents = students.filter(s => !s.parent_linked);

    return (
        <div className="space-y-8">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-2xl font-semibold mb-4">Linked Parents</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parent Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parent Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linked Student</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {parentLinks.length > 0 ? parentLinks.map((link, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{link.parent_first_name} {link.parent_last_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{link.parent_email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{link.student_first_name} {link.student_last_name}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={3} className="text-center py-4 text-gray-500">No parents are linked to students in this class yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-2xl font-semibold mb-4">Invite Parent for Students</h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student Name</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {unlinkedStudents.length > 0 ? unlinkedStudents.map(student => (
                                <tr key={student.user_id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{student.first_name} {student.last_name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button onClick={() => setInvitingStudent(student)} className="text-green-600 hover:text-green-900">
                                            Invite Parent
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={2} className="text-center py-4 text-gray-500">All students in this class have a linked parent.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
    
  return (
        <div className="w-full max-w-7xl">
            {viewingStudent && <StudentDetailModal student={viewingStudent} onClose={() => setViewingStudent(null)} />}
            {isCredentialsModalOpen && <GeneratedCredentialsModal credentials={generatedCredentials} onClose={() => setCredentialsModalOpen(false)} />}
            {invitingStudent && <InviteParentModal student={invitingStudent} onClose={() => setInvitingStudent(null)} onInviteSent={() => { setInvitingStudent(null); fetchStudentsForClass(selectedClass!.class_id); }} />}
            {studentToDelete && (
              <ConfirmationModal
                title="Delete Student"
                message={`Are you sure you want to permanently delete ${studentToDelete.first_name} ${studentToDelete.last_name}? This action cannot be undone.`}
                onConfirm={handleDeleteStudent}
                onCancel={() => setStudentToDelete(null)}
                confirmText="Delete"
                confirmColor="red"
              />
            )}
             {lessonToDelete && (
              <ConfirmationModal
                title="Delete Lesson"
                message={`Are you sure you want to permanently delete the lesson "${lessonToDelete.title}"? This will also unassign it from all classes.`}
                onConfirm={handleDeleteLesson}
                onCancel={() => setLessonToDelete(null)}
                confirmText="Delete"
                confirmColor="red"
              />
            )}
            {studentToReset && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h3 className="text-xl font-bold mb-4">Reset Password for {studentToReset.first_name}</h3>
                        <form onSubmit={submitPasswordReset}>
                            <PasswordOptions
                                passwordOption={passwordOption}
                                setPasswordOption={setPasswordOption}
                                customPassword={customPassword}
                                setCustomPassword={setCustomPassword}
                            />
                            <div className="flex justify-end space-x-2 mt-4">
                                <button type="button" onClick={() => setStudentToReset(null)} className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700">Reset Password</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <h2 className="text-3xl font-bold mb-2 text-gray-800">Teacher Dashboard</h2>
            <h3 className="text-xl font-semibold mb-6 text-gray-600">School: {user.school_name}</h3>
            
            <div className="mb-6 max-w-md">
                <label htmlFor="class-select" className="block text-sm font-medium text-gray-700">Select Class:</label>
                <select id="class-select" className="mt-1 block w-full p-2 border border-gray-300 rounded-md" value={selectedClass?.class_id || ""} onChange={(e) => setSelectedClass(classes.find(c => c.class_id === e.target.value) || null)}>
                    {classes.length > 0 ? classes.map(c => <option key={c.class_id} value={c.class_id}>{c.class_name}</option>) : <option value="">Please create a class</option>}
                </select>
            </div>
            
            {error && <div className="text-red-500 bg-red-100 p-3 rounded-md mb-4">{error}</div>}
            
            <div className="flex flex-wrap border-b border-gray-300 mb-6">
                <button className={`py-2 px-6 font-semibold ${tab === 'progress' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`} onClick={() => handleTabChange('progress')}>Student Progress</button>
                <button className={`py-2 px-6 font-semibold ${tab === 'analytics' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`} onClick={() => handleTabChange('analytics')}>Analytics</button>
                <button className={`py-2 px-6 font-semibold ${tab === 'builder' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`} onClick={() => handleTabChange('builder')}>Lesson Builder</button>
                <button className={`py-2 px-6 font-semibold ${tab === 'assign' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`} onClick={() => handleTabChange('assign')}>Assignments</button>
                <button className={`py-2 px-6 font-semibold ${tab === 'parents' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`} onClick={() => handleTabChange('parents')}>Parents</button>
                <button className={`py-2 px-6 font-semibold ${tab === 'sessions' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`} onClick={() => handleTabChange('sessions')}>Live Sessions</button>
                <button className={`py-2 px-6 font-semibold ${tab === 'manage' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`} onClick={() => handleTabChange('manage')}>Class Management</button>
            </div>
            
            {loading ? <LoadingSpinner /> : (
                 <div className="p-0">
                    {tab === 'progress' && selectedClass && renderProgressTab()}
                    {tab === 'analytics' && selectedClass && <AnalyticsTab data={analyticsData} students={students} onStudentClick={setViewingStudent} />}
                    {tab === 'builder' && renderBuilderTab()}
                    {tab === 'assign' && selectedClass && renderAssignTab()}
                    {tab === 'parents' && selectedClass && renderParentsTab()}
                    {tab === 'sessions' && renderLiveSessionsTab()}
                    {tab === 'manage' && renderManageTab()}
                 </div>
            )}
        </div>
    );
};

export default TeacherDashboard;